import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Monitor, Smartphone, LogOut, ShieldAlert, MailCheck, MailX, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { API_URL } from "../../lib/utils";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-AO", { dateStyle: "short", timeStyle: "short" }) : "—");
const EVENT_LABEL = { new_device_login: "Acesso de dispositivo novo", password_reset_requested: "Pedido de código de recuperação", password_reset: "Palavra-passe reposta por código" };
const isMobile = (label = "") => /iphone|ipad|android/i.test(label);

export const BackofficeSessionsDialog = ({ open, onOpenChange }) => {
  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, d, e] = await Promise.all([
        axios.get(`${API_URL}/backoffice/sessions`, { withCredentials: true }),
        axios.get(`${API_URL}/backoffice/devices`, { withCredentials: true }),
        axios.get(`${API_URL}/backoffice/security-events`, { withCredentials: true }),
      ]);
      setSessions(s.data); setDevices(d.data); setEvents(e.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao carregar sessões"); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(r?.data?.message || okMsg); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Operação falhou"); }
    finally { setBusy(""); }
  };

  const others = sessions.filter((s) => !s.current).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-2xl max-h-[85vh] overflow-y-auto grid-cols-[minmax(0,1fr)]" data-testid="backoffice-sessions-dialog">
        <DialogHeader className="min-w-0">
          <DialogTitle>Sessões e dispositivos</DialogTitle>
          <DialogDescription className="text-white/50">Quem está ligado ao Backoffice agora. Termine qualquer sessão à distância; recebe um email sempre que um dispositivo novo entra.</DialogDescription>
        </DialogHeader>

        <section className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-white/50">Sessões activas ({sessions.length})</h3>
            <Button size="sm" variant="outline" disabled={!others || busy === "all"} className="border-white/15 bg-transparent text-white/70 hover:bg-white/10 hover:text-white"
              onClick={() => act("all", () => axios.delete(`${API_URL}/backoffice/sessions`, { withCredentials: true }), "Outras sessões terminadas")} data-testid="sessions-terminate-others-btn">
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Terminar as outras ({others})
            </Button>
          </div>
          <ul className="space-y-1.5">
            {sessions.map((s) => (
              <li key={s.session_id} className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2 text-sm" data-testid={`session-row-${s.session_id}`}>
                {isMobile(s.device_label) ? <Smartphone className="w-4 h-4 text-cyan-400 shrink-0" /> : <Monitor className="w-4 h-4 text-cyan-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{s.device_label} {s.current && <Badge className="ml-1 bg-emerald-500/20 text-emerald-300 text-[10px]" data-testid="session-current-badge">Esta sessão</Badge>}</p>
                  <p className="text-xs text-white/40 break-words">IP {s.ip} · início {fmt(s.created_at)} · activa {fmt(s.last_seen_at || s.created_at)}</p>
                </div>
                {!s.current && (
                  <Button size="sm" variant="ghost" disabled={busy === s.session_id} className="text-red-300 hover:text-red-200 hover:bg-red-500/10"
                    onClick={() => act(s.session_id, () => axios.delete(`${API_URL}/backoffice/sessions/${s.session_id}`, { withCredentials: true }), "Sessão terminada")} data-testid={`session-terminate-${s.session_id}`}>
                    Terminar
                  </Button>
                )}
              </li>
            ))}
            {sessions.length === 0 && <li className="text-xs text-white/30">Sem sessões activas</li>}
          </ul>
        </section>

        <section className="space-y-2 min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-white/50">Dispositivos conhecidos ({devices.length})</h3>
          <ul className="space-y-1.5">
            {devices.map((d) => (
              <li key={d.device_id} className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2 text-sm" data-testid={`device-row-${d.device_id}`}>
                {isMobile(d.label) ? <Smartphone className="w-4 h-4 text-white/50 shrink-0" /> : <Monitor className="w-4 h-4 text-white/50 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="truncate">{d.label}</p>
                  <p className="text-xs text-white/40 break-words">1.º acesso {fmt(d.first_seen_at)} · último {fmt(d.last_seen_at)} · IP {d.last_ip} · {d.logins || 1} login(s)</p>
                </div>
                <Button size="sm" variant="ghost" disabled={busy === d.device_id} className="text-white/50 hover:text-white hover:bg-white/10"
                  onClick={() => act(d.device_id, () => axios.delete(`${API_URL}/backoffice/devices/${d.device_id}`, { withCredentials: true }), "Dispositivo esquecido")} data-testid={`device-forget-${d.device_id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Esquecer
                </Button>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-2 min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-white/50">Alertas de segurança</h3>
          <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="security-events-list">
            {events.map((e) => (
              <li key={e.event_id} className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2 text-xs">
                <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate text-white/80">{EVENT_LABEL[e.type] || e.type} · {e.device_label}</p>
                  <p className="text-white/40">{fmt(e.created_at)} · IP {e.ip}</p>
                </div>
                {e.email_status === "sent" && <span className="inline-flex items-center gap-1 text-emerald-300 shrink-0"><MailCheck className="w-3.5 h-3.5" /> email enviado</span>}
                {e.email_status === "failed" && <span className="inline-flex items-center gap-1 text-red-300 shrink-0" title={e.email_error}><MailX className="w-3.5 h-3.5" /> email falhou</span>}
                {e.email_status === "skipped" && <span className="text-white/30 shrink-0">sem email</span>}
              </li>
            ))}
            {events.length === 0 && <li className="text-xs text-white/30">Sem alertas registados</li>}
          </ul>
        </section>
      </DialogContent>
    </Dialog>
  );
};
